import {
    Alert,
    AlertIcon,
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    ModalBody,
    ModalFooter,
    Stack,
    Text,
    Textarea,
} from '@chakra-ui/react';
import Colors from '../../../config/colors';
import { useRef } from 'react';
import { ChakraStylesConfig, Select } from 'chakra-react-select';
import { useTranslation } from 'react-i18next';
import { TimeIcon } from '@chakra-ui/icons';

interface ICategory {
    label: string;
    value: number;
}

interface Props {
    productName: string;
    setProductName: (email: string) => void;
    errorMessage: string | null;
    category: ICategory;
    categoryList: ICategory[];
    selectCategory: (category: any) => void;
    setMessage: (message: string) => void;
    sendUserMessage: () => void;
    closeModal: () => void;
}

export default function SupportDialog({
    productName,
    setProductName,
    errorMessage,
    category,
    categoryList,
    selectCategory,
    setMessage,
    sendUserMessage,
    closeModal
}: Props) {
    const initialRef = useRef(null);
    const { t, i18n } = useTranslation();

    const chakraStyles: ChakraStylesConfig = {
        dropdownIndicator: (provided) => ({
            ...provided,
            p: 0,
            w: '50px',
            background: Colors.darkBlue2,
            border: 'none',
            color: '#fff',
        }),
        control: (provided, state) => ({
            ...provided,
            borderBottomLeftRadius: state.menuIsOpen ? 0 : 'md',
            borderBottomRightRadius: state.menuIsOpen ? 0 : 'md',
            transitionDuration: 0,
            background: Colors.darkBlue2,
            border: 'none',
            color: '#fff',
        }),
    };

    return (
        <>
            <ModalBody pb={6} display="flex" flexDirection="column" gap="5">
                <FormControl isRequired>
                    <FormLabel color="#fff">{t('what-product-are-you-looking-for')}</FormLabel>
                    <Input
                        ref={initialRef}
                        placeholder={t('name-of-the-desired-product')}
                        value={productName}
                        isDisabled={false}
                        onChange={(e) => setProductName(e.target.value)}
                        background={Colors.darkBlue2}
                        color={Colors.lightBlue}
                        border="none"
                    />
                </FormControl>
                <FormControl isRequired>
                    <FormLabel color="#fff">{t('category')}</FormLabel>
                    {categoryList?.length && (
                        <Select
                            isSearchable={false}
                            onChange={(option) => selectCategory(option)}
                            options={categoryList}
                            noOptionsMessage={({ inputValue }) => (
                                <Box>
                                    <Text>
                                        {t('such-category-does-not-exist')} &nbsp;
                                        <span style={{ fontWeight: '600' }}>«{inputValue}»</span>
                                        &nbsp; {t('does-not-exist')}
                                    </Text>
                                </Box>
                            )}
                            value={category}
                            chakraStyles={chakraStyles}
                        />
                    )}
                </FormControl>
                <FormControl>
                    <FormLabel color="#fff">{t('message')}</FormLabel>
                    <Textarea
                        placeholder={t('ask-us-a-question-about-the-product')}
                        height="150px"
                        onChange={(e) => setMessage(e.target.value)}
                        background={Colors.darkBlue2}
                        color={Colors.lightBlue}
                        border="none"
                    />
                </FormControl>
                {errorMessage && (
                    <Alert status="error">
                        <AlertIcon />
                        {errorMessage}
                    </Alert>
                )}
                {!errorMessage && (
                    <Box
                        width="100%"
                        borderRadius="8px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap="2"
                        padding="12px"
                        background={Colors.darkBlue2}
                        color={Colors.lightBlue}>
                        <Text>{t('we-respond-within-24-hours')}</Text>
                        <TimeIcon />
                    </Box>
                )}
            </ModalBody>

            <ModalFooter>
                <Stack direction={['row']} spacing="24px" color="#fff" width="100%">
                    <Button fontWeight="500" width="100%" onClick={() => closeModal()}>
                        {t('close')}
                    </Button>
                    <Button
                        width="100%"
                        color="#fff"
                        backgroundImage={
                            process.env.PUBLIC_URL + '/images/categories/background_spotlight.jpg'
                        }
                        backgroundSize="cover"
                        backgroundPosition="bottom"
                        fontWeight="500"
                        onClick={sendUserMessage}>
                        {t('send')}
                    </Button>
                </Stack>
            </ModalFooter>
        </>
    );
}
